import getConfig from 'next/config';

const DEFAULT_TIMEOUT_IN_MS = 5000;
const GRECAPTCHA_POLL_INTERVAL_IN_MS = 100;

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

async function waitForGrecaptcha(): Promise<void> {
  for (let attempt = 1; ; attempt += 1) {
    if ('grecaptcha' in window && window.grecaptcha !== undefined) {
      return;
    }

    await delay(GRECAPTCHA_POLL_INTERVAL_IN_MS);
  }
}

const grecaptchaReady = (): Promise<void> =>
  new Promise((resolve) => {
    (window as any).grecaptcha?.enterprise.ready(resolve);
  });

async function executeRecaptcha(
  siteKey: string,
  action: string,
): Promise<string> {
  await waitForGrecaptcha();
  await grecaptchaReady();
  return (window as any).grecaptcha.enterprise.execute(siteKey, { action });
}

async function getRecaptchaToken(
  action: string,
  timeoutInMs: number = DEFAULT_TIMEOUT_IN_MS,
): Promise<string> {
  const {
    publicRuntimeConfig: { GOOGLE_RECAPTCHA, INTEGRATION_TESTING },
  } = getConfig();

  if (INTEGRATION_TESTING) {
    return '';
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Recaptcha token request timed out'));
    }, timeoutInMs);
  });

  try {
    return await Promise.race([
      executeRecaptcha(GOOGLE_RECAPTCHA, action),
      timeoutPromise,
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}

export { getRecaptchaToken };
